import { ButtonProps as JoyButtonProps } from '@mui/joy/Button';
import { Calendar as CalendarIcon } from '@phosphor-icons/react';
import { mergeProps } from '@react-aria/utils';
import clsx from 'clsx';
import { format, isDate, toDate } from 'date-fns';
import * as React from 'react';
import {
  DayPickerRangeProps,
  DayPickerSingleProps,
  isDateRange,
} from 'react-day-picker';
import { FieldPath, FieldValues } from 'react-hook-form';

import { Button } from './button';
import { Calendar } from './calendar';
import { Description, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  useFormController,
} from './form';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { createSplitProps } from './utils';

export function DatePicker({
  size = 'md',
  variant = 'outlined',
  color = 'neutral',
  placeholder = <span>Select date</span>,
  controlled = true,
  defaultOpen = false,
  disabled = false,
  disabledSlots,
  ...calendarProps
}: Pick<JoyButtonProps, 'size' | 'variant' | 'color' | 'disabled'> & {
  placeholder?: React.ReactNode;
  controlled?: boolean;
  defaultOpen?: boolean;
  disabledSlots?: DayPickerSingleProps['disabled'];
} & Omit<DayPickerSingleProps, 'mode'>) {
  const [open, setOpen] = React.useState(defaultOpen);

  const controller = useFormController();
  const value =
    controller?.field.value &&
    isDate(toDate(controller?.field.value)) &&
    controlled
      ? toDate(controller.field.value)
      : calendarProps.selected;
  const hasError = !!controller?.fieldState.invalid;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          ref={controller?.field.ref}
          data-slot='control'
          size={size}
          color={color}
          variant={variant}
          sx={{
            '--variant-borderWidth': '1px',
            borderColor: (theme) =>
              hasError ? theme.palette.danger[500] : undefined,
            justifyContent: 'space-between',
            px: (theme) => theme.spacing(1.5),
            fontWeight: 500,
            fontSize: (theme) => theme.fontSize.md,
            color: (theme) =>
              value ? theme.palette.neutral[500] : theme.palette.neutral[300],
            ':active': {
              backgroundColor: (theme) => theme.palette.neutral[50],
            },
          }}
          disabled={disabled}
          endDecorator={<CalendarIcon />}
          fullWidth
        >
          {value ? format(value, 'PPP') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto z-10 relative' bleed>
        <Calendar
          initialFocus
          {...mergeProps(
            calendarProps,
            { disabled: disabledSlots },
            {
              onSelect: () => {
                setOpen(false);
              },
            } as Partial<DayPickerSingleProps>,
            controlled && controller
              ? ({
                  id: controller.field.name,
                  selected: controller.field.value,
                  onSelect: controller.field.onChange,
                } as Partial<DayPickerSingleProps>)
              : {},
          )}
          mode='single'
        />
      </PopoverContent>
    </Popover>
  );
}

export function DatePickerField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  ...props
}: ComposedFormControllerProps<T, TName> &
  React.ComponentPropsWithoutRef<typeof DatePicker> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, controlProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);
  return (
    <FormController {...controllerProps}>
      <div
        className={clsx([
          '[&>[data-slot=label]+[data-slot=control]]:mt-2',
          '[&>[data-slot=label]+[data-slot=description]]:mt-1',
          '[&>[data-slot=description]+[data-slot=control]]:mt-2',
          '[&>[data-slot=control]+[data-slot=description]]:mt-2',
          '[&>[data-slot=control]+[data-slot=error]]:mt-2',
          '[&>[data-slot=label]]:font-medium',
        ])}
      >
        {label && <Label>{label}</Label>}
        <DatePicker
          {...controlProps}
          size={controllerProps.size}
          disabled={props.disabled}
        />
        {description && <Description>{description}</Description>}
      </div>
    </FormController>
  );
}

export function DateRangePicker({
  size = 'md',
  variant = 'outlined',
  color = 'neutral',
  placeholder = <span>Select date</span>,
  controlled = true,
  defaultOpen = false,
  value: controlValue,
  disabled,
  disabledSlots,
  ...calendarProps
}: Pick<JoyButtonProps, 'size' | 'variant' | 'color' | 'disabled'> & {
  placeholder?: React.ReactNode;
  controlled?: boolean;
  defaultOpen?: boolean;
  disabledSlots?: DayPickerRangeProps['disabled'];
  value?: DayPickerRangeProps['selected'];
} & Omit<DayPickerRangeProps, 'mode' | 'disabled' | 'selected'>) {
  const [open, setOpen] = React.useState(defaultOpen);

  const controller = useFormController();
  const value =
    controller?.field.value &&
    isDateRange(controller?.field.value) &&
    controlled
      ? controller.field.value
      : controlValue;
  const hasError = !!controller?.fieldState.invalid;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          data-slot='control'
          ref={controller?.field.ref}
          size={size}
          color={color}
          variant={variant}
          sx={{
            '--variant-borderWidth': '1px',
            borderColor: (theme) =>
              hasError ? theme.palette.danger[500] : undefined,
            justifyContent: 'space-between',
            px: (theme) => theme.spacing(1.5),
            fontWeight: 500,
            fontSize: (theme) => theme.fontSize.md,
            color: (theme) =>
              value ? theme.palette.neutral[500] : theme.palette.neutral[300],
            ':active': {
              backgroundColor: (theme) => theme.palette.neutral[50],
            },
            width: '100%',
            whiteSpace: 'nowrap',
          }}
          endDecorator={<CalendarIcon />}
          disabled={disabled}
        >
          {value?.from && value?.to ? (
            <>
              {format(value.from, 'LLL dd, y')} -{' '}
              {format(value.to, 'LLL dd, y')}
            </>
          ) : value?.from ? (
            format(value.from, 'LLL dd, y')
          ) : (
            placeholder
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto data-[state=open]:z-10' bleed>
        <Calendar
          {...mergeProps(
            calendarProps,
            {
              value,
              // onSelect: (e) => {
              //   if (isDateRange(e)) {
              //     setOpen(false);
              //   }
              // },
              disabled: disabledSlots,
            } as Partial<DayPickerRangeProps>,
            controlled && controller
              ? ({
                  selected: controller?.field.value,
                  id: controller?.field.name,
                  onSelect: (range) => {
                    controller?.field.onChange(range);
                    controller?.field.onBlur();
                  },
                } as Partial<DayPickerRangeProps>)
              : {},
          )}
          mode='range'
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export function DateRangePickerField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  ...props
}: ComposedFormControllerProps<T, TName> &
  React.ComponentPropsWithoutRef<typeof DateRangePicker> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, controlProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);
  return (
    <FormController {...controllerProps}>
      <div
        className={clsx([
          '[&>[data-slot=label]+[data-slot=control]]:mt-2',
          '[&>[data-slot=label]+[data-slot=description]]:mt-1',
          '[&>[data-slot=description]+[data-slot=control]]:mt-2',
          '[&>[data-slot=control]+[data-slot=description]]:mt-2',
          '[&>[data-slot=control]+[data-slot=error]]:mt-2',
          '[&>[data-slot=label]]:font-medium',
        ])}
      >
        {label && <Label>{label}</Label>}
        <DateRangePicker {...controlProps} />
        {description && <Description>{description}</Description>}
      </div>
    </FormController>
  );
}
